import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { Dictionary, Guid } from "DiscoveryEngine.TS/Utils";
import { ProtocolSettingsTypes, ProtocolSettings, SnmpVersion, SnmpSettings } from "DiscoveryEngine.TS/Protocols";
import { SchedDiscConfigComponentBase } from "../SchedDiscConfigComponentBase";
import { NetFacadeService } from 'netview';
import { DiscResultsService } from 'discovery';
import { DisplayDeviceListDto, DisplayDeviceListEntryDto } from '../../../UvExplorerServer.Dtos.TS/Session/DisplayDeviceListDto';


export class ProtocolSettingsData {
	public Guid: string;
	public Selected: boolean;
	public Name: string;
	public ProtocolType: string;
	public UseCount: number;

	constructor(guid: string, selected: boolean, name: string, protoType: string, useCount: number) {
		this.Guid = guid;
		this.Selected = selected;
		this.Name = name;
		this.ProtocolType = protoType;
		this.UseCount = useCount;
	}
}


@Component({
    selector: 'sched-disc-config-protocols',
	templateUrl: './sched-disc-config-protocols.component.html',
	styleUrls: ['./sched-disc-config-protocols.component.scss']
})
export class SchedDiscConfigProtocolsComponent extends SchedDiscConfigComponentBase {

	public EnableCliDiscovery: UntypedFormControl;
	public FullWindowsInventory: UntypedFormControl;

	private _AllProtocolSettings: ProtocolSettings[];
	private _ProtocolSettingsData: ProtocolSettingsData[];

	private deviceListChangedSub: Subscription;

	private protocolCounts: Dictionary<string, number>;

	constructor(private fb: UntypedFormBuilder, netFacadeService: NetFacadeService,
		private discResultsService: DiscResultsService)
	{
		super(netFacadeService);
		this._AllProtocolSettings = null;
		this._ProtocolSettingsData = null;

		this.deviceListChangedSub = discResultsService.onDeviceListLoaded.subscribe(
			(deviceList: DisplayDeviceListDto) => {
				this.setDeviceList(deviceList);
			},
			(error?) => {
				this.setDeviceList(null);
			});

		this.BuildForm();
	}

	protected BuildForm(): void {
		let enableCliDiscovery = false;
		let fullWindowsInventory = false;

		if (this.Settings) {
			enableCliDiscovery = this.Settings.EnableCliDiscovery;
			fullWindowsInventory = this.Settings.WinFullInventory;
		}

		this.EnableCliDiscovery = new UntypedFormControl(enableCliDiscovery);
		this.FullWindowsInventory = new UntypedFormControl(fullWindowsInventory + "", Validators.required);

		this.enableDisableControls();

		this.SettingsForm = this.fb.group({
			EnableCliDiscovery: this.EnableCliDiscovery,
			FullWindowsInventory: this.FullWindowsInventory,
		});
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.FullWindowsInventory, this.WindowsSettingsSelected);
	}

	private setDeviceList(deviceList: DisplayDeviceListDto): void {
		this.protocolCounts = new Dictionary<string, number>();

		if (deviceList) {
			deviceList.entries.forEach((device: DisplayDeviceListEntryDto) => {
				if (device.protocolGuids && (device.protocolGuids.length > 0)) {
					device.protocolGuids.forEach((protocolGuid: string) => {
						if (this.protocolCounts.ContainsKey(protocolGuid)) {
							let tmpCount: number = this.protocolCounts.Item(protocolGuid) as number;
							this.protocolCounts.Add(protocolGuid, tmpCount + 1);
						}
						else {
							this.protocolCounts.Add(protocolGuid, 1);
						}
					});
				}
			});
		}

		// RESET the UseCount
		if (this._AllProtocolSettings && (this._AllProtocolSettings.length > 0)) {
			this._AllProtocolSettings.forEach((protoSettings: ProtocolSettings) => {
				protoSettings.UseCount = 0;
			});
		}

		if ((this.protocolCounts.Count > 0) && this._AllProtocolSettings) {
			this._AllProtocolSettings.forEach((protoSettings: ProtocolSettings) => {
				if (protoSettings.HasGUID) {
					if (this.protocolCounts.ContainsKey(protoSettings.GUID)) {
						protoSettings.UseCount = this.protocolCounts.Item(protoSettings.GUID);
					}
					else {
						protoSettings.UseCount = 0;
					}
				}
			});
		}

		this.RebuildProtocolSettingsData();
	}


	private get AllProtocolSettings(): ProtocolSettings[] {
		if (!this._AllProtocolSettings) {
			this._AllProtocolSettings = [];

			if (this.Network) {
				this._AllProtocolSettings = this.Network.ProtocolSettings.ToArray();
			}
		}
		return this._AllProtocolSettings;
	}

	private FindProtocolSettingsByGuid(guid: string): ProtocolSettings {
		guid = guid.toUpperCase();
		let result: ProtocolSettings = null;
		this.AllProtocolSettings.forEach((settings) => {
			if (settings.GUID.toUpperCase() == guid)
				result = settings;
		});
		return result;
	}

	public get ProtocolSettingsData(): ProtocolSettingsData[] {
		if (!this._ProtocolSettingsData && this.Settings) {
			this._ProtocolSettingsData = [];

			this.Settings.ProtocolSettings.ForEach((val: Guid) => {
				let settings = this.FindProtocolSettingsByGuid(val.GuidStr);
				if (settings) {
					this._ProtocolSettingsData.push(
						new ProtocolSettingsData(settings.GUID, true, settings.Name,
							this.GetProtocolDisplayType(settings), settings.UseCount));
				}
			});

			this.AllProtocolSettings.forEach((settings) => {
				let guid = Guid.FromString(settings.GUID);
				if (!this.Settings.ProtocolSettings.Contains(guid)) {
					this._ProtocolSettingsData.push(
						new ProtocolSettingsData(settings.GUID, false, settings.Name,
							this.GetProtocolDisplayType(settings), settings.UseCount));
				}
			});
		}
		return this._ProtocolSettingsData;
	}

	public get HasProtocolSettingsData(): boolean {
		return (this._ProtocolSettingsData && this.ProtocolSettingsData.length > 0);
	}

	public CanMoveUp(data: ProtocolSettingsData): boolean {
		let result = false;
		if (this._ProtocolSettingsData) {
			let index = this._ProtocolSettingsData.indexOf(data);
			result = (index > 0);
		}
		return result;
	}

	public CanMoveDown(data: ProtocolSettingsData): boolean {
		let result = false;
		if (this._ProtocolSettingsData) {
			let index = this._ProtocolSettingsData.indexOf(data);
			result = (index >= 0 && index < (this._ProtocolSettingsData.length - 1));
		}
		return result;
	}

	public MoveUp(data: ProtocolSettingsData): void {
		if (this.CanMoveUp(data)) {
			let index = this._ProtocolSettingsData.indexOf(data);
			this.Swap(this._ProtocolSettingsData, index, index - 1);
		}
	}

	public MoveDown(data: ProtocolSettingsData): void {
		if (this.CanMoveDown(data)) {
			let index = this._ProtocolSettingsData.indexOf(data);
			this.Swap(this._ProtocolSettingsData, index, index + 1);
		}
	}

	public get WindowsSettingsSelected(): boolean {
		let result = false;
		if (this._ProtocolSettingsData) {
			this._ProtocolSettingsData.forEach((data) => {
				if (data.Selected) {
					let ps = this.FindProtocolSettingsByGuid(data.Guid);
					if (ps && ps.SettingsType == ProtocolSettingsTypes.Windows) {
						result = true;
					}
				}
			});
		}
		return result;
	}

	private Swap<T>(arr: T[], index1: number, index2: number): void {
		let tmp: T = arr[index1];
		arr[index1] = arr[index2];
		arr[index2] = tmp;

		// Trigger change detection
		this._ProtocolSettingsData = [...this._ProtocolSettingsData];

		// Mark the form as dirty
		this.SettingsForm.markAsDirty();
	}

	private GetProtocolDisplayType(protoSettings: ProtocolSettings): string {
		let protoTypeStr = "";

		if (protoSettings) {
			switch (protoSettings.SettingsType) {
				case ProtocolSettingsTypes.Snmp:
					{
						// DEFAULT
						protoTypeStr = "SNMP";

						let snmpSettings = protoSettings as SnmpSettings;
						if (snmpSettings) {
							// REFINE
							if (snmpSettings.Version == SnmpVersion.Ver1)
								protoTypeStr = "SNMPv1";
							else if (snmpSettings.Version == SnmpVersion.Ver2)
								protoTypeStr = "SNMPv2/SNMPv2C";
							else if (snmpSettings.Version == SnmpVersion.Ver3)
								protoTypeStr = "SNMPv3";
						}
					}
					break;

				case ProtocolSettingsTypes.Vim:
					protoTypeStr = "VMware";
					break;

				case ProtocolSettingsTypes.Telnet:
					protoTypeStr = "Telnet";
					break;

				case ProtocolSettingsTypes.Ssh:
					protoTypeStr = "SSH";
					break;

				case ProtocolSettingsTypes.Windows:
					protoTypeStr = "Windows";
					break;

				case ProtocolSettingsTypes.Ping:
					protoTypeStr = "Ping";
					break;

				case ProtocolSettingsTypes.Prtg:
					protoTypeStr = "PRTG";
					break;

				case ProtocolSettingsTypes.HTTP:
					protoTypeStr = "HTTP";
					break;

				default:
					console.assert(false);
					break;
			}
		}

		return protoTypeStr;
	}

	public get SelectAllState(): any {
		let selectionState = false;
		if (this.HasProtocolSettingsData) {
			let selected = 0;
			let unselected = 0;
			this._ProtocolSettingsData.forEach((data) => {
				if (data.Selected)
					++selected;
				else
					++unselected;
			});
			if (selected == 0 && unselected == 0)
				selectionState = false;
			else if (selected == 0)
				selectionState = false;
			else if (unselected == 0)
				selectionState = true;
			else
				selectionState = false;
		}
		return selectionState;
	}

	public SelectAllChanged(event: any): void {
		if (this.SelectAllState)
			this.DeselectAllSettings();
		else
			this.SelectAllSettings();

		this.enableDisableControls();

		this.SettingsForm.markAsDirty();
	}

	private SelectAllSettings(): void {
		if (this._ProtocolSettingsData) {
			this._ProtocolSettingsData.forEach((data) => data.Selected = true);
		}
	}

	private DeselectAllSettings(): void {
		if (this._ProtocolSettingsData) {
			this._ProtocolSettingsData.forEach((data) => data.Selected = false);
		}
	}

	public SettingsSelectionChanged(event: any, data: ProtocolSettingsData): void {
		data.Selected = !data.Selected;

		this.enableDisableControls();

		this.SettingsForm.markAsDirty();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.onChanges();
		this.RebuildProtocolSettingsData();
		this.BuildForm();
	}

	private RebuildProtocolSettingsData(): void {
		// Force rebuild of _ProtocolSettingsData
		this._ProtocolSettingsData = null;
		let tmp = this.ProtocolSettingsData;
	}

	public applyTempChanges(): void {
		if (this.Settings) {
			this.Settings.EnableCliDiscovery = (this.EnableCliDiscovery.value);
			this.Settings.WinFullInventory = (this.FullWindowsInventory.value === "true");

			let psSet = this.Settings.ProtocolSettings;
			psSet.Clear();
			this._ProtocolSettingsData.forEach((data) => {
				if (data.Selected) {
					psSet.AddProtoSettingsID(Guid.FromString(data.Guid));
				}
			});

			this.BuildForm();
			this.onApplyTempChanges();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Protocols / Credentials"];
	}

	public AddSettings() {

		// TODO
	}
}
